import type {
  ArtifactHistoryItem,
  DestinationHistoryItem,
  DocumentHistoryItem,
  DocumentSearchMode,
  DocumentTab,
  FragmentHighlight,
  LotoPanelState,
  RegionHighlight,
  SearchHistoryItem,
  SearchStateUpdate,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { SelectedTab } from 'features/navbar/types'
import {
  CLEAR_SEARCH_VIEWER_PARAMS,
  type DocumentsSearchParams,
  urlSearchParamsFromColumnFilters,
} from 'routes/documents/-searchParams'

import {
  getKeepAliveSearchHistoryEntry,
  isSearchTabVisible,
} from './keepAliveSearchTab'
import { sameStringArray } from './sameStringArray'
import { parseSelectedRegionsParam } from './selectedRegionsParam'

export const CLEAR_DOCUMENT_PARAMS: DocumentsSearchParams = {
  documentID: undefined,
  pageNumber: undefined,
  selectedBoxes: undefined,
  selectedRegions: undefined,
  searchFilterVendor: undefined,
  searchFilterDocType: undefined,
}

export const CLEAR_ALL_VIEWER_PARAMS: DocumentsSearchParams = {
  ...CLEAR_DOCUMENT_PARAMS,
  ...CLEAR_SEARCH_VIEWER_PARAMS,
}

const DEFAULT_SUBMITTED_DOCUMENT_SEARCH_MODE: DocumentSearchMode = 'ai'

/** Maps search tab state to URL search params (clear document keys separately). */
export function searchParamsFromSearchState(
  searchState: SearchHistoryItem['searchState']
): Pick<
  DocumentsSearchParams,
  | 'searchQuery'
  | 'searchInputQuery'
  | 'searchFilterTitle'
  | 'searchFilterDocumentTitle'
  | 'searchFilterSystem'
  | 'searchFilterDrawing'
  | 'searchFilterVendor'
  | 'searchFilterDocType'
  | 'searchTab'
  | 'searchBrowseAll'
> {
  const inputQuery =
    searchState.inputQuery !== undefined &&
    searchState.inputQuery !== searchState.query
      ? searchState.inputQuery
      : undefined

  return {
    searchQuery: searchState.query || undefined,
    searchInputQuery: inputQuery,
    ...urlSearchParamsFromColumnFilters(searchState.filters),
    searchTab: searchState.contentFilter === 'sensors' ? 'sensors' : undefined,
    searchBrowseAll: searchState.browseAll === true ? true : undefined,
  }
}

function isNoopSearchStateUpdate(
  prev: SearchHistoryItem['searchState'],
  updates: Partial<SearchHistoryItem['searchState']>
): boolean {
  const keys = Object.keys(updates)
  if (keys.length === 0) return true
  const prevValues: Record<string, unknown> = { ...prev }
  const nextValues: Record<string, unknown> = { ...updates }
  return keys.every((key) => prevValues[key] === nextValues[key])
}

export function mergeSearchStateIntoTabs(
  prevTabs: DocumentTab[],
  tabId: string,
  activeTabId: string,
  searchStateUpdates: SearchStateUpdate
): { nextTabs: DocumentTab[]; urlSync?: SearchHistoryItem['searchState'] } {
  let urlSync: SearchHistoryItem['searchState'] | undefined
  let didChange = false
  const nextTabs = prevTabs.map((tab) => {
    if (tab.id !== tabId) return tab
    const retainedSearch = getKeepAliveSearchHistoryEntry(tab)
    if (!retainedSearch) return tab
    const { item: searchItem, index: historyIndex } = retainedSearch
    const updates =
      typeof searchStateUpdates === 'function'
        ? searchStateUpdates(searchItem.searchState)
        : searchStateUpdates
    const prev = searchItem.searchState
    if (isNoopSearchStateUpdate(prev, updates)) {
      return tab
    }
    didChange = true
    const merged: SearchHistoryItem['searchState'] = {
      ...searchItem.searchState,
      ...updates,
    }
    const shouldSyncUrl =
      'query' in updates ||
      'filters' in updates ||
      'contentFilter' in updates ||
      'browseAll' in updates
    if (isSearchTabVisible(tab, activeTabId) && shouldSyncUrl) {
      urlSync = merged
    }
    const updatedItem: SearchHistoryItem = {
      ...searchItem,
      searchState: merged,
    }
    const newHistory = [...tab.history]
    newHistory[historyIndex] = updatedItem
    return { ...tab, history: newHistory }
  })
  return { nextTabs: didChange ? nextTabs : prevTabs, urlSync }
}

/**
 * Chat drafts are not written to the URL (typing does not sync). A reload URL
 * still has the empty composer from submit and must not wipe unsent text.
 */
function urlSearchPatchForHistoryItem(
  item: SearchHistoryItem,
  patch: Partial<SearchHistoryItem['searchState']>
): Partial<SearchHistoryItem['searchState']> {
  if (item.searchState.chatOnly !== true || patch.inputQuery) return patch
  const nextPatch = { ...patch }
  delete nextPatch.inputQuery
  return nextPatch
}

/**
 * Apply a URL search patch to the active tab. If that tab is not a search tab,
 * open a fresh search tab so deep-linked params are not dropped by URL sync.
 */
export function applySearchUrlPatchToTabs(
  tabs: DocumentTab[],
  activeId: string,
  patch: Partial<SearchHistoryItem['searchState']>
): { openTabs: DocumentTab[]; activeTabId: string } {
  const tab = tabs.find((t) => t.id === activeId) ?? tabs[0]
  if (!tab) return { openTabs: tabs, activeTabId: activeId }
  const item = tab.history[tab.currentIndex]
  if (item.type !== 'search') {
    const freshItem = createDefaultSearchHistoryItem()
    const searchTab: DocumentTab = {
      ...createDefaultTab(),
      history: [
        {
          ...freshItem,
          searchState: { ...freshItem.searchState, ...patch },
        },
      ],
    }
    return { openTabs: [...tabs, searchTab], activeTabId: searchTab.id }
  }
  const searchPatch = urlSearchPatchForHistoryItem(item, patch)
  const updatedItem: SearchHistoryItem = {
    ...item,
    searchState: { ...item.searchState, ...searchPatch },
  }
  const newHistory = [...tab.history]
  newHistory[tab.currentIndex] = updatedItem
  return {
    openTabs: tabs.map((t) =>
      t.id === tab.id ? { ...t, history: newHistory } : t
    ),
    activeTabId: tab.id,
  }
}

function searchItemHoldsQuery(item: TabHistoryItem, query: string): boolean {
  if (item.type !== 'search') return false
  if (item.searchState.query === query) return true
  return (
    item.searchState.docChat?.conversation.turns.some(
      (turn) => turn.query === query
    ) ?? false
  )
}

/**
 * Back/forward lands on search URLs written for an earlier state of a tab.
 * Return to the search the user left rather than submitting the URL query as a
 * new search; `null` keeps the current tabs.
 */
export function restoreSearchTabForHistoryTraversal(
  tabs: DocumentTab[],
  activeId: string,
  query: string | undefined
): { openTabs: DocumentTab[]; activeTabId: string } | null {
  const activeTab = tabs.find((t) => t.id === activeId)
  if (!activeTab) return null
  if (activeTab.history[activeTab.currentIndex].type === 'search') return null

  const previousSearchIndex = activeTab.history.findLastIndex(
    (item, index) => index < activeTab.currentIndex && item.type === 'search'
  )
  if (previousSearchIndex !== -1) {
    return {
      openTabs: tabs.map((t) =>
        t.id === activeTab.id ? { ...t, currentIndex: previousSearchIndex } : t
      ),
      activeTabId: activeTab.id,
    }
  }

  if (!query) return null
  const owner = tabs.find((t) =>
    searchItemHoldsQuery(t.history[t.currentIndex], query)
  )
  return owner ? { openTabs: tabs, activeTabId: owner.id } : null
}

export const createDefaultSearchHistoryItem = (): SearchHistoryItem => ({
  type: 'search',
  searchState: {
    contentFilter: 'documents',
    searchMode: DEFAULT_SUBMITTED_DOCUMENT_SEARCH_MODE,
    viewMode: 'card',
    query: '',
    filters: [],
    sorting: [],
    pagination: { pageIndex: 0, pageSize: 200 },
    selectedDocumentId: null,
  },
})

export const createDefaultTab = (id?: string): DocumentTab => ({
  id: id ?? `new-tab-${crypto.randomUUID()}`,
  history: [createDefaultSearchHistoryItem()],
  currentIndex: 0,
})

export const createWorkspaceHomeTab = (): DocumentTab => ({
  ...createDefaultTab(),
  workspaceDestination: SelectedTab.DocumentsHome,
})

export const createBrowseAllDocumentsTab = (): DocumentTab => {
  const searchItem = createDefaultSearchHistoryItem()
  return {
    id: `documents-tab-${crypto.randomUUID()}`,
    history: [
      {
        ...searchItem,
        searchState: {
          ...searchItem.searchState,
          searchMode: 'normal',
          browseAll: true,
        },
      },
    ],
    currentIndex: 0,
    workspaceDestination: SelectedTab.Documents,
  }
}

export const createDestinationTab = ({
  destinationKey,
  label,
  href,
}: Omit<DestinationHistoryItem, 'type'>): DocumentTab => ({
  id: `destination-tab-${crypto.randomUUID()}`,
  history: [{ type: 'destination', destinationKey, label, href }],
  currentIndex: 0,
})

export const createDocumentTab = (
  documentId: string,
  pageNumber: number = 1,
  selectedBoxIds: string[] = [],
  query: string = '',
  fragmentHighlight?: FragmentHighlight,
  searchOriginQuery?: string,
  regionHighlight?: RegionHighlight,
  selectedGraphNodeId?: string,
  lotoPanel?: LotoPanelState
): DocumentTab => {
  const docItem: DocumentHistoryItem = {
    type: 'document',
    documentId,
    pageNumber,
    selectedBoxIds,
    query,
    ...(searchOriginQuery && { searchOriginQuery }),
    ...(fragmentHighlight && { fragmentHighlight }),
    ...(regionHighlight && { regionHighlight }),
    ...(selectedGraphNodeId && { selectedGraphNodeId }),
    ...(lotoPanel && { lotoPanel }),
  }
  return {
    id: `tab-${crypto.randomUUID()}`,
    history: [docItem],
    currentIndex: 0,
  }
}

export const artifactResourceKey = (artifactId: string): string =>
  `artifact:${artifactId}`

/** Full-width tab for a produced document; the artifact body is fetched, never stored here. */
export const createArtifactTab = ({
  artifactId,
  title,
}: Omit<ArtifactHistoryItem, 'type'> & { title?: string }): DocumentTab => ({
  id: `artifact-tab-${crypto.randomUUID()}`,
  history: [{ type: 'artifact', artifactId }],
  currentIndex: 0,
  ...(title ? { customLabel: title } : {}),
  workspaceDestination: SelectedTab.Documents,
  resourceKey: artifactResourceKey(artifactId),
})

export const createInitialTab = (
  documentID?: string,
  pageNumber?: number,
  selectedBoxes?: string[],
  selectedRegions?: string
): DocumentTab => {
  if (documentID) {
    const regionBoxes = parseSelectedRegionsParam(selectedRegions)
    const docItem: DocumentHistoryItem = {
      type: 'document',
      documentId: documentID,
      pageNumber: pageNumber ?? 1,
      selectedBoxIds: selectedBoxes ?? [],
      query: '',
      ...(regionBoxes.length > 0 && {
        regionHighlight: { pageNum: pageNumber ?? 1, boxes: regionBoxes },
      }),
    }
    return {
      id: `tab-${crypto.randomUUID()}`,
      history: [createDefaultSearchHistoryItem(), docItem],
      currentIndex: 1,
    }
  }
  return createDefaultTab()
}

const sameRegionHighlight = (
  a: RegionHighlight | undefined,
  b: RegionHighlight | undefined
): boolean => {
  if (a === undefined && b === undefined) return true
  if (a === undefined || b === undefined) return false
  if (a.pageNum !== b.pageNum || a.boxes.length !== b.boxes.length) return false
  return a.boxes.every(
    (box, index) =>
      box.left === b.boxes[index]?.left &&
      box.top === b.boxes[index]?.top &&
      box.width === b.boxes[index]?.width &&
      box.height === b.boxes[index]?.height
  )
}

export function applyDocumentUrlIntentToTabs(
  openTabs: DocumentTab[],
  tabId: string,
  search: Pick<
    DocumentsSearchParams,
    'pageNumber' | 'selectedBoxes' | 'selectedRegions'
  >
): DocumentTab[] {
  const hasPageNumber = search.pageNumber !== undefined
  const hasSelectedBoxes = search.selectedBoxes !== undefined
  const hasSelectedRegions = search.selectedRegions !== undefined
  if (!hasPageNumber && !hasSelectedBoxes && !hasSelectedRegions) {
    return openTabs
  }

  let didChange = false
  const nextTabs = openTabs.map((tab) => {
    if (tab.id !== tabId) return tab
    const currentItem = tab.history[tab.currentIndex]
    if (currentItem.type !== 'document') return tab

    const pageNumber = hasPageNumber
      ? (search.pageNumber ?? currentItem.pageNumber)
      : currentItem.pageNumber
    const selectedBoxIds = hasSelectedBoxes
      ? (search.selectedBoxes ?? currentItem.selectedBoxIds)
      : currentItem.selectedBoxIds
    const pageChanged = pageNumber !== currentItem.pageNumber

    let regionHighlight = currentItem.regionHighlight
    if (hasSelectedRegions) {
      const regionBoxes = parseSelectedRegionsParam(search.selectedRegions)
      regionHighlight =
        regionBoxes.length > 0
          ? { pageNum: pageNumber, boxes: regionBoxes }
          : undefined
    } else if (pageChanged) {
      regionHighlight = undefined
    }

    const fragmentHighlight = pageChanged
      ? undefined
      : currentItem.fragmentHighlight

    if (
      pageNumber === currentItem.pageNumber &&
      sameStringArray(selectedBoxIds, currentItem.selectedBoxIds) &&
      sameRegionHighlight(regionHighlight, currentItem.regionHighlight) &&
      fragmentHighlight === currentItem.fragmentHighlight
    ) {
      return tab
    }

    didChange = true
    const updatedItem: DocumentHistoryItem = {
      ...currentItem,
      pageNumber,
      selectedBoxIds,
      ...(hasSelectedRegions || pageChanged ? { regionHighlight } : {}),
      ...(pageChanged ? { fragmentHighlight } : {}),
    }
    const newHistory = [...tab.history]
    newHistory[tab.currentIndex] = updatedItem
    return { ...tab, history: newHistory }
  })

  return didChange ? nextTabs : openTabs
}
